import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import { fetchClientZipMap } from '@src/api';
import { formatDateToYMD } from '@src/utils/dateUtils';

const MAP_COLORS = ['#1d4ed8', '#c026d3', '#059669', '#d97706', '#0d9488', '#9333ea', '#f43f5e', '#5c6bc0'];
const DEFAULT_CENTER = [37.8, -96];
const MAP_HEIGHT = 640;
const FULLSCREEN_MAP_HEIGHT = '80vh';
const LEAD_MARKER_STYLE = {
  minRadius: 1,
  maxRadius: 12,
  scale: 2.2,
  zoomMultiplier: 1.18,
  baselineZoom: 6
};
const NORTH_AMERICA_BOUNDS = [
  [5, -170],
  [84, -52]
];
const OUT_OF_AREA_COLOR = '#c62828';

const getTerritoryKey = (territoryId) => (territoryId === null || territoryId === undefined ? '__none__' : String(territoryId));

const withAlpha = (hex, alpha) => {
  if (!hex) return `rgba(84, 110, 122, ${alpha})`;
  let sanitized = hex.replace('#', '');
  if (sanitized.length === 3) {
    sanitized = sanitized
      .split('')
      .map((ch) => ch + ch)
      .join('');
  }
  const int = parseInt(sanitized, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const normalizeGeometry = (geometry) => {
  if (!geometry) {
    return null;
  }
  if (geometry.type === 'Feature' || geometry.type === 'FeatureCollection') {
    return geometry;
  }
  if (geometry.geometry) {
    return geometry;
  }
  return {
    type: 'Feature',
    properties: {},
    geometry
  };
};

const MapAutoCenter = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center);
    }
  }, [center, map]);
  return null;
};

MapAutoCenter.propTypes = {
  center: PropTypes.arrayOf(PropTypes.number).isRequired
};

const MapZoomWatcher = ({ onZoomChange }) => {
  useMapEvents({
    zoomend: (event) => {
      const zoom = event.target.getZoom();
      onZoomChange(zoom);
    }
  });
  return null;
};

MapZoomWatcher.propTypes = {
  onZoomChange: PropTypes.func.isRequired
};

const ClientZipMap = ({
  clientId,
  parentCompanyId,
  clientIds = [],
  startDate,
  endDate
}) => {
  const [mapData, setMapData] = useState({ zips: [], territories: [], leads: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapZoom, setMapZoom] = useState(LEAD_MARKER_STYLE.baselineZoom);
  const [isFullMapOpen, setIsFullMapOpen] = useState(false);

  useEffect(() => {
    const normalizedClientIds = Array.isArray(clientIds)
      ? clientIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id))
      : [];

    if (!clientId && !parentCompanyId && !normalizedClientIds.length) return;

    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate)
        };
        if (clientId) {
          params.client_id = clientId;
        }
        if (parentCompanyId) {
          params.parent_company_id = parentCompanyId;
        }
        if (normalizedClientIds.length) {
          params.client_ids = normalizedClientIds.join(',');
        }
        const response = await fetchClientZipMap(params);
        if (!isMounted) return;
        setMapData({
          zips: response.data?.zips || [],
          territories: response.data?.territories || [],
          leads: response.data?.leads || []
        });
      } catch (err) {
        console.error('Failed to load client ZIP map data', err);
        if (isMounted) {
          setError('Unable to load map data right now.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [clientId, parentCompanyId, clientIds, startDate, endDate]);

  const territoryLookup = useMemo(() => {
    const lookup = {};
    mapData.territories.forEach((territory, index) => {
      const key = getTerritoryKey(territory.territory_id);
      lookup[key] = {
        ...territory,
        color: MAP_COLORS[index % MAP_COLORS.length]
      };
    });
    return lookup;
  }, [mapData.territories]);

  const resolveTerritoryColor = useCallback(
    (territoryId) => territoryLookup[getTerritoryKey(territoryId)]?.color || '#546e7a',
    [territoryLookup]
  );

  const leadZipMarkers = useMemo(() => {
    if (!Array.isArray(mapData.leads)) {
      return [];
    }

    return mapData.leads
      .map((bucket) => {
        const lat = Number(bucket.latitude);
        const lng = Number(bucket.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          return null;
        }
        const count = Number(bucket.lead_count) || 0;
        const baseRadius = LEAD_MARKER_STYLE.minRadius + Math.log(count + 1) * LEAD_MARKER_STYLE.scale;
        const zoomFactor = LEAD_MARKER_STYLE.zoomMultiplier ** (mapZoom - LEAD_MARKER_STYLE.baselineZoom);
        const scaledRadius = baseRadius * zoomFactor;
        const radius = Math.min(LEAD_MARKER_STYLE.maxRadius, Math.max(LEAD_MARKER_STYLE.minRadius, scaledRadius));
        const color = bucket.has_out_of_area
          ? OUT_OF_AREA_COLOR
          : resolveTerritoryColor(bucket.client_territory_id);
        return {
          key: bucket.zip_code || bucket.client_zip_vertical_id || `${lat}|${lng}`,
          lat,
          lng,
          radius: Number.isFinite(radius) ? radius : 6,
          color,
          bucket
        };
      })
      .filter(Boolean);
  }, [mapData.leads, mapZoom, resolveTerritoryColor]);

  const zipFeatureCollection = useMemo(() => {
    const features = [];
    const appendFeature = (baseProps, feature) => {
      if (!feature || !feature.geometry) {
        return;
      }
      features.push({
        type: 'Feature',
        geometry: feature.geometry,
        properties: {
          ...feature.properties,
          ...baseProps
        }
      });
    };

    mapData.zips.forEach((zip) => {
      const geometry = zip.geometry_simplified || zip.geometry;
      const normalized = normalizeGeometry(geometry);
      if (!normalized) {
        return;
      }
      const props = {
        zipId: zip.id,
        zipCode: zip.zip_code,
        territoryId: zip.territory_id,
        territoryName: zip.territory_name || 'Unassigned',
        isFiltered: Boolean(zip.is_filtered_out)
      };

      if (normalized.type === 'FeatureCollection' && Array.isArray(normalized.features)) {
        normalized.features.forEach((feature) => appendFeature(props, feature));
      } else if (normalized.type === 'Feature') {
        appendFeature(props, normalized);
      }
    });

    return {
      type: 'FeatureCollection',
      features
    };
  }, [mapData.zips]);

  const zipStyle = useCallback(
    (feature = {}) => {
      const props = feature.properties || {};
      const baseColor = resolveTerritoryColor(props.territoryId);
      const fillColor = props.isFiltered ? withAlpha(baseColor, 0.18) : withAlpha(baseColor, 0.45);
      return {
        color: baseColor,
        weight: 1,
        fillColor,
        fillOpacity: props.isFiltered ? 0.2 : 0.5
      };
    },
    [resolveTerritoryColor]
  );

  const handleZipEachFeature = useCallback((feature, layer) => {
    const props = feature?.properties || {};
    const tooltipHtml = `
      <div>
        <div><strong>ZIP:</strong> ${props.zipCode || ''}</div>
        <div><strong>Territory:</strong> ${props.territoryName || 'Unassigned'}</div>
        <div><strong>Status:</strong> ${props.isFiltered ? 'Filtered' : 'Active'}</div>
      </div>
    `;
    layer.bindTooltip(tooltipHtml);
  }, []);

  const mapCenter = useMemo(() => {
    if (leadZipMarkers.length > 0) {
      return [leadZipMarkers[0].lat, leadZipMarkers[0].lng];
    }
    const zipWithCentroid = mapData.zips.find((zip) => zip.centroid);
    if (zipWithCentroid) {
      return [zipWithCentroid.centroid.lat, zipWithCentroid.centroid.lng];
    }
    return DEFAULT_CENTER;
  }, [leadZipMarkers, mapData.zips]);

  const renderLegend = () => {
    if (!mapData.territories.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No ZIP boundaries are available for this client yet.
        </Typography>
      );
    }

    return (
      <Stack direction="row" flexWrap="wrap" spacing={2} rowGap={1}>
        {mapData.territories.map((territory) => {
          const color = resolveTerritoryColor(territory.territory_id);
          return (
            <Stack key={`${territory.territory_id || 'none'}`} direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '2px',
                  backgroundColor: color,
                  border: '1px solid rgba(0,0,0,0.2)'
                }}
              />
              <Typography variant="body2">
                {territory.territory_name} ({territory.zip_count})
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    );
  };

  if (!clientId) {
    return null;
  }

  const renderMap = (height, boxProps = {}) => (
    <Box sx={{ height, ...boxProps }}>
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        minZoom={3}
        maxBounds={NORTH_AMERICA_BOUNDS}
        maxBoundsViscosity={1}
        scrollWheelZoom
        style={{ height: '100%', width: '100%' }}
      >
        <MapAutoCenter center={mapCenter} />
        <MapZoomWatcher onZoomChange={setMapZoom} />
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
        {zipFeatureCollection.features.length > 0 && (
          <GeoJSON data={zipFeatureCollection} style={zipStyle} onEachFeature={handleZipEachFeature} />
        )}
        {leadZipMarkers.map((point) => (
          <CircleMarker
            key={point.key}
            center={[point.lat, point.lng]}
            radius={point.radius}
            pathOptions={{
              color: 'transparent',
              weight: 0,
              fillColor: point.color,
              fillOpacity: 0.85
            }}
          >
            <Tooltip direction="top" offset={[0, -point.radius]}>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  ZIP {point.bucket.zip_code || 'Unknown'}
                </Typography>
                <Typography variant="body2">
                  Leads: {point.bucket.lead_count}{' '}
                  {point.bucket.out_of_area_count > 0 ? `(Out-of-area: ${point.bucket.out_of_area_count})` : ''}
                </Typography>
                <Typography variant="body2">
                  Territory:{' '}
                  {point.bucket.has_out_of_area ? 'Out Of Area' : point.bucket.client_territory_name || 'Unassigned'}
                </Typography>
                {point.bucket.sample_lead && (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                      Latest Lead
                    </Typography>
                    <Typography variant="body2">{point.bucket.sample_lead.full_name || 'Lead'}</Typography>
                    <Typography variant="body2">
                      {point.bucket.sample_lead.city}, {point.bucket.sample_lead.state} {point.bucket.sample_lead.zip_code}
                    </Typography>
                    <Typography variant="body2">
                      {point.bucket.sample_lead.created_at
                        ? new Date(point.bucket.sample_lead.created_at).toLocaleString()
                        : 'Date unavailable'}
                    </Typography>
                    <Typography variant="body2">Source: {point.bucket.sample_lead.source || 'N/A'}</Typography>
                  </>
                )}
              </Box>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </Box>
  );

  return (
    <Card>
      <CardHeader
        title="Client Territory Map"
        subheader="ZIP shapes are shaded by territory. Filtered ZIPs appear lighter, and red dots highlight ZIPs with out-of-area leads."
        action={
          <Button size="small" onClick={() => setIsFullMapOpen(true)} disabled={loading || !!error}>
            View Full Map
          </Button>
        }
      />
      <CardContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {renderLegend()}
            {mapData.territories.length > 0 || leadZipMarkers.length > 0 ? (
              renderMap(MAP_HEIGHT, { mt: 2 })
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No qualifying ZIPs or leads were found for this selection.
              </Typography>
            )}
          </>
        )}
      </CardContent>
      <Dialog fullScreen open={isFullMapOpen} onClose={() => setIsFullMapOpen(false)}>
        <DialogTitle>Client Territory Map</DialogTitle>
        <DialogContent sx={{ p: 0 }}>{renderMap(FULLSCREEN_MAP_HEIGHT)}</DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFullMapOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

ClientZipMap.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parentCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clientIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  startDate: PropTypes.instanceOf(Date).isRequired,
  endDate: PropTypes.instanceOf(Date).isRequired
};

export default ClientZipMap;
